import { useCallback, useState } from 'react';
import { CloseSquareOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row } from 'antd';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { TPersonData } from 'app/railways/domains/passengers/core/person';
import { confirmPersonPhoneRequest } from 'app/railways/domains/passengers/requests';

import { EPhoneStatus } from '../../core/phone-status';

export const ContactStatusFields = observer(() => {
  return (
    <Row gutter={16}>
      <Col span={24}>
        <Form.Item shouldUpdate={true} label="Статус телефона">
          {({ getFieldValue }) => {
            const phone = getFieldValue(['phone']);
            if (!phone) return <b>Номер телефона отсутствует</b>;

            const phoneStatus = getFieldValue(['phoneStatus']);
            if (phoneStatus === EPhoneStatus.confirmed)
              return (
                <div style={{ color: 'green' }}>Номер телефона подтвержден</div>
              );

            const personId = getFieldValue(['id']);
            return (
              <PersonPhoneConfirmField phone={phone} personId={personId} />
            );
          }}
        </Form.Item>
      </Col>
    </Row>
  );
});

type ConfirmSteps = 'submitPhone' | 'submitCode' | 'final';

const PersonPhoneConfirmField = observer(
  ({ phone, personId }: { phone: string; personId: number }) => {
    const [currentStep] = useState(() =>
      observable.box<ConfirmSteps>('submitPhone'),
    );

    const pageNotify = usePageNotify();

    const sendPhoneResource = useResource<{ id: TPersonData['id'] }, void>({
      request: confirmPersonPhoneRequest,
      errorNotify: pageNotify,
      onDone() {
        currentStep.set('submitCode');
      },
    });

    const sendCodeResource = useResource<
      { id: TPersonData['id']; code: string },
      void
    >({
      request: confirmPersonPhoneRequest,
      errorNotify: pageNotify,

      onDone() {
        currentStep.set('final');
      },
    });

    const [form] = Form.useForm();

    const resetConfirmPhone = () => currentStep.set('submitCode');

    const handlePhoneSubmit = useCallback(() => {
      sendPhoneResource.send({ id: personId });
    }, [personId, sendPhoneResource]);

    const handleCodeSubmit = useCallback(
      ({ code }: { code: string }) => {
        sendCodeResource.send({ id: personId, code });
      },
      [personId, sendCodeResource],
    );

    switch (currentStep.get()) {
      case 'submitPhone':
        return (
          <>
            <Form
              form={form}
              initialValues={{ phone }}
              layout="vertical"
              onFinish={handlePhoneSubmit}
              component={false}
            >
              <Row gutter={8}>
                <Col>
                  <Form.Item
                    name="phone"
                    validateStatus="error"
                    help="Не подтвержден"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item>
                    <Button
                      onClick={form.submit}
                      loading={sendPhoneResource.isLoading}
                    >
                      Подтвердить
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            <div>
              После нажатия кнопки "Подтвердить", будет отправлен проверочный
              код на указанный номер телефона
            </div>
          </>
        );
      case 'submitCode':
        return (
          <>
            <Form
              form={form}
              initialValues={{ phone, code: '' }}
              layout="vertical"
              onFinish={handleCodeSubmit}
              component={false}
            >
              <Row gutter={8}>
                <Col>
                  <Form.Item
                    name="phone"
                    validateStatus="validating"
                    help="Ожидает подтверждения"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col>
                  <Form.Item name="code">
                    <Input />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item>
                    <Button
                      onClick={form.submit}
                      loading={sendCodeResource.isLoading}
                    >
                      Отправить
                    </Button>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item>
                    <Button
                      icon={<CloseSquareOutlined />}
                      onClick={() => resetConfirmPhone()}
                    ></Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            <div>
              Введите код из СМС, который был отправлен на указанный номер
            </div>
          </>
        );
      case 'final':
      default:
        return null;
    }
  },
);
